import {
  Combobox,
  ComboboxItem,
  Loader,
  SelectProps,
  useCombobox,
  InputBase,
  InputBaseProps,
} from "@mantine/core"
import { useTranslation } from "react-i18next"
import "./scroll-container/index.css"

export interface DefaultSingleSelectProps
  extends Omit<SelectProps, "renderOption" | "onChange"> {
  options: ComboboxItem[]
  loading?: boolean
  onSelectionChange?: (selectedValues: string | null) => void
  onSearchChange?: (searchValue: string) => void
  renderOption?: (item: ComboboxItem) => JSX.Element
  onScrolledToBottom?: () => void
  noOptionsFoundText?: string
  inputProps?: InputBaseProps
}

export function DefaultSingleSelect({
  options,
  loading,
  searchValue,
  onSelectionChange,
  onScrolledToBottom,
  onSearchChange,
  renderOption,
  label,
  value,
  placeholder,
  noOptionsFoundText,
  inputProps,
  ...selectProps
}: DefaultSingleSelectProps) {
  const [internalSearchValue, setinternalSearchValue] = React.useState(
    searchValue ?? ""
  )

  React.useEffect(
    () => onSearchChange && onSearchChange(internalSearchValue),
    [internalSearchValue]
  )

  React.useEffect(() => {
    setinternalSearchValue(searchValue ?? "")
  }, [searchValue])

  const [selectedValues, setSelectedValues] = React.useState<string | null>(
    value ?? null
  )
  const [selectedLabel, setSelectedLabel] = React.useState<string | null>(
    options.find((o) => o.value === value)?.label ?? null
  )

  React.useEffect(() => {
    onSelectionChange && onSelectionChange(selectedValues)
  }, [selectedValues])

  React.useEffect(() => {
    if (value === null || value === undefined) {
      setSelectedValues(null)
      setSelectedLabel(null)
      setinternalSearchValue("")
    }
  }, [value])

  React.useEffect(() => {
    if (selectedLabel === null && !!selectedValues) {
      const matchingOption = options.find((o) => o.value === selectedValues)
      if (matchingOption) {
        setSelectedLabel(matchingOption.label)
      }
    }
  }, [options])

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const handleValueSelect = (val: string) => {
    const option = options.find((o) => o.value === val)
    setSelectedLabel(option?.label ?? null)
    setSelectedValues(val)
    setinternalSearchValue("")
    combobox.closeDropdown()
  }

  const handleValueRemove = (_: string) => {
    setSelectedValues(null)
    setSelectedLabel(null)
    setinternalSearchValue("")
  }

  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (
      combobox.dropdownOpened &&
      inputRef.current &&
      document.activeElement !== inputRef.current
    ) {
      inputRef.current.focus()
    }
  }, [combobox.dropdownOpened])

  React.useEffect(() => {
    if (
      combobox.dropdownOpened &&
      options.length === 0 &&
      inputRef.current &&
      document.activeElement !== inputRef.current
    ) {
      inputRef.current.focus()
    }
  }, [combobox.dropdownOpened, options.length])

  const { t } = useTranslation("app_server")

  return (
    <Combobox onOptionSubmit={handleValueSelect} store={combobox}>
      <Combobox.Target targetType="input">
        <Combobox.EventsTarget>
          <InputBase
            error={selectProps.error}
            withAsterisk={!!selectProps?.required}
            ref={inputRef}
            label={label}
            rightSection={
              value ? (
                <Combobox.ClearButton
                  onClear={() => handleValueRemove(value)}
                />
              ) : (
                <Combobox.Chevron />
              )
            }
            rightSectionPointerEvents={value ? undefined : "none"}
            value={
              combobox.dropdownOpened
                ? internalSearchValue
                : selectedLabel || ""
            }
            onFocus={() => combobox.openDropdown()}
            onChange={(event) => {
              combobox.updateSelectedOptionIndex()
              setinternalSearchValue(event.currentTarget.value)
              combobox.openDropdown()
            }}
            onClick={() => combobox.openDropdown()}
            placeholder={placeholder}
            {...inputProps}
          />
        </Combobox.EventsTarget>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          <div
            className="custom-scroll-container"
            style={{ maxHeight: 300, overflowY: "auto" }}
            onScroll={(event) => {
              const scrollArea = event.currentTarget
              if (
                scrollArea.scrollHeight -
                  scrollArea.scrollTop -
                  scrollArea.clientHeight <
                  10 &&
                onScrolledToBottom
              ) {
                onScrolledToBottom()
              }
            }}
          >
            {options.length === 0 ? (
              <Combobox.Empty>
                {noOptionsFoundText || t("NoOptionsFound")}
              </Combobox.Empty>
            ) : (
              options.map((option) => {
                return renderOption ? (
                  renderOption(option)
                ) : (
                  <Combobox.Option
                    {...option}
                    key={option.value}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                      display: "block",
                    }}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        width: "100%",
                      }}
                    >
                      {option.label}
                    </span>
                  </Combobox.Option>
                )
              })
            )}
            {loading && (
              <div style={{ padding: 8 }}>
                <Loader size="xs" />
              </div>
            )}
          </div>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
